/**
 * ObjectExplorer — lewy panel przeglądarki bazy: drzewo obiektów schematu tenanta.
 *
 * Klik w obiekt → szczegóły w zakładkach (Właściwości/SQL/Statystyki/Zależności).
 * Prawy przycisk na tabeli/widoku → menu akcji (Query Tool, Wyświetl dane, Skrypty…).
 * Wyszukiwarka filtruje po nazwie obiektu i kolumny, rozwijając ścieżki do trafień.
 */

import { type KeyboardEvent, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Database, RefreshCw, Table2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import {
  buildDbTree,
  type DbTreeLabels,
  type DbTreeNode,
  defaultExpanded,
  filterDbTree,
} from '@/lib/db-browser/tree';
import type { DbSchemaSnapshot } from '@/lib/db-browser/types';
import { cn } from '@/lib/utils';

import { DbObjectIcon } from './DbObjectIcon';

export type ExplorerAction =
  | 'query_tool'
  | 'view_first'
  | 'view_last'
  | 'view_all'
  | 'view_filter'
  | 'script_select'
  | 'script_create'
  | 'properties'
  | 'copy_name';

interface ObjectExplorerProps {
  snapshot: DbSchemaSnapshot;
  selectedId: string | null;
  onSelect: (node: DbTreeNode) => void;
  onAction: (action: ExplorerAction, node: DbTreeNode) => void;
  onRefresh: () => void;
  refreshing?: boolean;
}

interface FlatRow {
  node: DbTreeNode;
  depth: number;
  expanded: boolean;
  hasChildren: boolean;
}

function flatten(
  node: DbTreeNode,
  depth: number,
  expanded: Set<string>,
  out: FlatRow[],
): FlatRow[] {
  const hasChildren = !!node.children?.length;
  const isOpen = hasChildren && expanded.has(node.id);
  out.push({ node, depth, expanded: isOpen, hasChildren });
  if (isOpen) node.children!.forEach((c) => flatten(c, depth + 1, expanded, out));
  return out;
}

export function ObjectExplorer({
  snapshot,
  selectedId,
  onSelect,
  onAction,
  onRefresh,
  refreshing,
}: ObjectExplorerProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => defaultExpanded(snapshot));
  const [menu, setMenu] = useState<{ node: DbTreeNode; x: number; y: number } | null>(null);

  const labels: DbTreeLabels = useMemo(
    () => ({
      schemas: t('admin.dbBrowser.tree.schemas'),
      groups: {
        tables: t('admin.dbBrowser.tree.tables'),
        views: t('admin.dbBrowser.tree.views'),
        matviews: t('admin.dbBrowser.tree.matviews'),
        functions: t('admin.dbBrowser.tree.functions'),
        procedures: t('admin.dbBrowser.tree.procedures'),
        triggerFunctions: t('admin.dbBrowser.tree.triggerFunctions'),
        sequences: t('admin.dbBrowser.tree.sequences'),
        types: t('admin.dbBrowser.tree.types'),
      },
      columns: t('admin.dbBrowser.tree.columns'),
      constraints: t('admin.dbBrowser.tree.constraints'),
      indexes: t('admin.dbBrowser.tree.indexes'),
      triggers: t('admin.dbBrowser.tree.triggers'),
    }),
    [t],
  );

  const root = useMemo(() => buildDbTree(snapshot, labels), [snapshot, labels]);
  const filtered = useMemo(() => filterDbTree(root, query), [root, query]);
  const effectiveExpanded = query.trim() ? filtered.expand : expanded;
  const rows = useMemo(
    () => (filtered.tree ? flatten(filtered.tree, 0, effectiveExpanded, []) : []),
    [filtered.tree, effectiveExpanded],
  );

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onRowKey = (e: KeyboardEvent, row: FlatRow) => {
    if (e.key === 'ArrowRight' && row.hasChildren && !row.expanded) {
      e.preventDefault();
      toggle(row.node.id);
    } else if (e.key === 'ArrowLeft' && row.expanded) {
      e.preventDefault();
      toggle(row.node.id);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const items = Array.from(
        (e.currentTarget.closest('[role="tree"]') as HTMLElement).querySelectorAll<HTMLElement>(
          '[role="treeitem"]',
        ),
      );
      const i = items.indexOf(e.currentTarget as HTMLElement);
      items[e.key === 'ArrowDown' ? i + 1 : i - 1]?.focus();
    } else if (
      (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) &&
      isRelation(row.node)
    ) {
      // Menu z klawiatury — jak prawy przycisk myszy.
      e.preventDefault();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMenu({ node: row.node, x: rect.left + 40, y: rect.bottom });
    }
  };

  const onRowContext = (e: MouseEvent, node: DbTreeNode) => {
    if (!isRelation(node)) return;
    e.preventDefault();
    onSelect(node);
    setMenu({ node, x: e.clientX, y: e.clientY });
  };

  const selectedNode = rows.find((r) => r.node.id === selectedId)?.node;

  return (
    <aside
      aria-label={t('admin.dbBrowser.explorer.title')}
      className="flex h-full min-h-0 flex-col border-r border-border-secondary bg-bg-primary"
    >
      <div className="flex h-9 items-center gap-1 border-b border-border-secondary bg-bg-secondary px-2">
        <span className="pl-1 text-[13px] font-semibold text-text-primary">
          {t('admin.dbBrowser.explorer.title')}
        </span>
        <div className="flex-1" />
        <IconButton
          size="sm"
          aria-label={t('admin.dbBrowser.explorer.queryTool')}
          title={t('admin.dbBrowser.explorer.queryTool')}
          onClick={() => onAction('query_tool', selectedNode ?? root)}
        >
          <Database />
        </IconButton>
        <IconButton
          size="sm"
          aria-label={t('admin.dbBrowser.explorer.viewData')}
          title={t('admin.dbBrowser.explorer.viewData')}
          disabled={!selectedNode || !isRelation(selectedNode)}
          onClick={() => selectedNode && onAction('view_first', selectedNode)}
        >
          <Table2 />
        </IconButton>
        <IconButton
          size="sm"
          aria-label={t('admin.dbBrowser.explorer.refresh')}
          title={t('admin.dbBrowser.explorer.refresh')}
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshCw className={cn(refreshing && 'animate-spin')} />
        </IconButton>
      </div>
      <div className="border-b border-border-secondary p-2">
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('admin.dbBrowser.explorer.searchPlaceholder')}
          aria-label={t('admin.dbBrowser.explorer.searchLabel')}
        />
      </div>
      <div
        role="tree"
        aria-label={t('admin.dbBrowser.explorer.title')}
        className="min-h-0 flex-1 overflow-auto px-1 py-1"
      >
        {rows.length === 0 && (
          <p className="px-3 py-4 text-sm text-text-tertiary">
            {t('admin.dbBrowser.explorer.noMatches')}
          </p>
        )}
        {rows.map((row) => (
          <Button
            key={row.node.id}
            variant="ghost"
            role="treeitem"
            aria-level={row.depth + 1}
            aria-expanded={row.hasChildren ? row.expanded : undefined}
            aria-selected={row.node.id === selectedId}
            data-node-id={row.node.id}
            onClick={() => {
              if (row.hasChildren && !query.trim()) toggle(row.node.id);
              if (row.node.ref || !row.hasChildren) onSelect(row.node);
            }}
            onDoubleClick={() => row.node.insertText && onAction('copy_name', row.node)}
            onContextMenu={(e) => onRowContext(e, row.node)}
            onKeyDown={(e) => onRowKey(e, row)}
            className={cn(
              'h-6 w-full justify-start gap-1.5 rounded px-1 text-[13px] font-normal',
              row.node.id === selectedId &&
                'bg-accent-50 text-accent-700 hover:bg-accent-100 dark:bg-accent-900/30',
            )}
            style={{ paddingLeft: 4 + row.depth * 14 }}
          >
            <span
              className="inline-flex w-3 shrink-0 justify-center text-text-tertiary"
              aria-hidden="true"
            >
              {row.hasChildren ? (
                row.expanded ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )
              ) : null}
            </span>
            <DbObjectIcon icon={row.node.icon} />
            <span className="truncate">{row.node.label}</span>
            {row.node.count !== undefined && (
              <span className="shrink-0 text-xs text-text-tertiary">({row.node.count})</span>
            )}
            {row.node.meta && (
              <span className="ml-auto shrink-0 pl-2 font-mono text-[11px] text-text-tertiary">
                {row.node.meta}
              </span>
            )}
          </Button>
        ))}
      </div>
      {menu && (
        <ExplorerContextMenu
          node={menu.node}
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          onAction={(a) => {
            setMenu(null);
            onAction(a, menu.node);
          }}
        />
      )}
    </aside>
  );
}

function isRelation(node: DbTreeNode): boolean {
  return (
    !!node.ref &&
    (node.ref.kind === 'table' || node.ref.kind === 'view' || node.ref.kind === 'matview')
  );
}

function ExplorerContextMenu({
  node,
  x,
  y,
  onClose,
  onAction,
}: {
  node: DbTreeNode;
  x: number;
  y: number;
  onClose: () => void;
  onAction: (a: ExplorerAction) => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose();
    };
    const onDown = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown);
    ref.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);

  const W = 240;
  const H = 360;
  const left = x + W > window.innerWidth ? x - W : x;
  const top = y + H > window.innerHeight ? Math.max(8, y - H) : y;

  const item = (action: ExplorerAction, label: string, indent = false) => (
    <Button
      key={action}
      variant="ghost"
      role="menuitem"
      onClick={() => onAction(action)}
      className={cn(
        'h-7 w-full justify-start rounded-none px-3 text-[13px] font-normal',
        indent && 'pl-6',
      )}
    >
      {label}
    </Button>
  );
  const heading = (label: string) => (
    <div className="px-3 pb-0.5 pt-1.5 text-[11px] font-medium text-text-tertiary">{label}</div>
  );
  const sep = <div className="my-1 border-t border-border-secondary" />;

  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={t('admin.dbBrowser.menu.label', { name: node.label })}
      data-testid="db-explorer-menu"
      className="fixed z-[10000] w-[240px] rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
      style={{ left, top }}
    >
      <div className="truncate px-3 py-1 font-mono text-xs text-text-secondary">{node.label}</div>
      {sep}
      {item('query_tool', t('admin.dbBrowser.menu.queryTool'))}
      {heading(t('admin.dbBrowser.menu.viewData'))}
      {item('view_first', t('admin.dbBrowser.menu.viewFirst'), true)}
      {item('view_last', t('admin.dbBrowser.menu.viewLast'), true)}
      {item('view_all', t('admin.dbBrowser.menu.viewAll'), true)}
      {item('view_filter', t('admin.dbBrowser.menu.viewFilter'), true)}
      {heading(t('admin.dbBrowser.menu.scripts'))}
      {item('script_select', t('admin.dbBrowser.menu.scriptSelect'), true)}
      {item('script_create', t('admin.dbBrowser.menu.scriptCreate'), true)}
      {sep}
      {item('copy_name', t('admin.dbBrowser.menu.copyName'))}
      {item('properties', t('admin.dbBrowser.menu.properties'))}
    </div>,
    document.body,
  );
}
